"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import {
	GlassBorder,
	type GlassBorderProps,
	GlassBorderPanel,
	type GlassBorderPanelProps,
	type GlassBorderRadius,
	type GlassBorderTone,
	type GlassBorderVariant,
} from "./glass-border";

/**
 * Slightly opaque fill behind engagement rail / cover meta chips — sits inside
 * the frosted {@link GlassBorder} rim (same pattern as topic social rail).
 */
export const GLASS_PANEL_SURFACE_CLASS = cn(
	"bg-zinc-100/95 text-foreground dark:bg-zinc-900/90 dark:text-white",
);

/**
 * Softer frosted rim for compact circle / pill chrome (header circles, wallet /
 * player HUDs). Matches equipment block hairline (`border-black/10` /
 * `dark:border-white/10`) without darkening global {@link GlassBorder} system tone.
 */
export const GLASS_CHROME_SOFT_PLATE_CLASS = cn(
	"bg-black/10 shadow-none",
	"dark:bg-white/10 dark:shadow-none",
);

/**
 * Solid hairline shell matching equipment / backpack panels
 * (`border-black/10 dark:border-white/10`). Use via
 * {@link GlassBorderProps.shellBorderClassName} when the default system
 * `black_6%` shell should read like those blocks (player menu, wallets).
 */
export const GLASS_BLOCK_HAIRLINE_SHELL_BORDER_CLASS = cn(
	"border border-black/10",
	"dark:border-white/10",
);

/** Drop the hairline shell so only the frosted band remains (no double rim). */
export const GLASS_CHROME_DEDUPE_SHELL_BORDER_CLASS = "border-0";

/** Pair with {@link GLASS_CHROME_DEDUPE_SHELL_BORDER_CLASS} so radius math matches. */
export const GLASS_CHROME_DEDUPE_SHELL_STYLE = {
	["--glass-shell-bw" as string]: "0px",
} as const;

export interface GlassPanelProps {
	children: ReactNode;
	/** Outer {@link GlassBorder} shell class. */
	className?: string;
	wrapperClassName?: string;
	contentClassName?: string;
	/** Merged onto {@link GlassBorderPanel} (after default surface fill when enabled). */
	panelClassName?: string;
	/** Forwarded to {@link GlassBorderPanel} (e.g. `nav`, `data-testid`). */
	panelProps?: Omit<GlassBorderPanelProps, "children" | "className">;
	tone?: GlassBorderTone;
	radius?: GlassBorderRadius;
	borderWidth?: number;
	largeBlur?: boolean;
	variant?: GlassBorderVariant;
	/**
	 * Apply {@link GLASS_PANEL_SURFACE_CLASS} on the inner panel.
	 * @default true
	 */
	surface?: boolean;
	plateClassName?: GlassBorderProps["plateClassName"];
	shellBorderClassName?: GlassBorderProps["shellBorderClassName"];
	cornerRadii?: GlassBorderProps["cornerRadii"];
	edgeBorderWidths?: GlassBorderProps["edgeBorderWidths"];
	style?: GlassBorderProps["style"];
	/**
	 * When `false`, allow children to paint outside the rounded clip
	 * (header wallet bag / player avatar). @default true
	 */
	clipContent?: boolean;
}

/**
 * Convenience shell: {@link GlassBorder} + {@link GlassBorderPanel}.
 * Used behind engagement buttons and cover meta chips.
 */
export function GlassPanel({
	children,
	className,
	wrapperClassName,
	contentClassName,
	panelClassName,
	panelProps,
	tone = "system",
	radius = "2xl",
	borderWidth = 1,
	largeBlur = true,
	variant = "outer",
	surface = true,
	plateClassName,
	shellBorderClassName,
	cornerRadii,
	edgeBorderWidths,
	style,
	clipContent = true,
}: GlassPanelProps) {
	return (
		<GlassBorder
			variant={variant}
			tone={tone}
			radius={radius}
			borderWidth={borderWidth}
			largeBlur={largeBlur}
			plateClassName={plateClassName}
			shellBorderClassName={shellBorderClassName}
			cornerRadii={cornerRadii}
			edgeBorderWidths={edgeBorderWidths}
			style={style}
			clipContent={clipContent}
			className={cn("w-max overflow-visible", className)}
			wrapperClassName={wrapperClassName}
			contentClassName={cn("border-transparent", contentClassName)}
		>
			<GlassBorderPanel
				{...panelProps}
				className={cn(
					surface && GLASS_PANEL_SURFACE_CLASS,
					panelClassName,
				)}
			>
				{children}
			</GlassBorderPanel>
		</GlassBorder>
	);
}

export interface GlassCircleChromeProps {
	children: ReactNode;
	/** Outer size utilities (e.g. `size-10`, `size-[34px]`). */
	sizeClassName?: string;
	className?: string;
	panelClassName?: string;
	plateClassName?: string;
	tone?: GlassBorderTone;
	borderWidth?: number;
	largeBlur?: boolean;
}

/**
 * Per-control circular {@link GlassPanel} — engagement-rail chrome on a single
 * circle button (no shared panel behind a group).
 *
 * Soft plate + no hairline shell — shell + frosted band reads as a double rim
 * on small discs; matches header wallet / player HUD pills.
 */
export function GlassCircleChrome({
	children,
	sizeClassName = "size-10",
	className,
	panelClassName,
	plateClassName,
	tone = "system",
	borderWidth = 1,
	largeBlur = true,
}: GlassCircleChromeProps) {
	return (
		<GlassPanel
			variant="inner"
			radius="full"
			tone={tone}
			borderWidth={borderWidth}
			largeBlur={largeBlur}
			plateClassName={plateClassName ?? GLASS_CHROME_SOFT_PLATE_CLASS}
			shellBorderClassName={GLASS_CHROME_DEDUPE_SHELL_BORDER_CLASS}
			style={GLASS_CHROME_DEDUPE_SHELL_STYLE}
			className={cn("shrink-0 overflow-hidden", sizeClassName, className)}
			wrapperClassName={cn("h-full min-h-0 w-full min-w-0", sizeClassName)}
			contentClassName="h-full w-full border-transparent"
			panelClassName={cn(
				"grid size-full place-items-center overflow-hidden rounded-full p-0",
				panelClassName,
			)}
		>
			{children}
		</GlassPanel>
	);
}
